import { Chip, Divider, FormControl, Grid, IconButton, TextField, Typography } from '@mui/material';
import {
  getIssueTypes,
  getProperties,
  IssueType,
  Property,
} from 'vendor-marketplace-shared-library';
import { Dispatch, SetStateAction, useState } from 'react';
import { DropdownWithCheckboxes } from '../common/DropdownWithCheckboxes';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import Modal from '../common/Modal';
import ProcureVendorContactForm from './ProcureVendorContactForm';
import GoogleAddressForm from '../common/GoogleAddressForm';
import PrimaryButton from '../common/styled-mui-overrides/PrimaryButton';
import { useQuery } from '@apollo/client';
import { ProcureVendorContactInput, ProcureVendorFormInput } from '../../types/vendors';
import { AddressInput } from '../../types/google-maps';

type VendorFormProps = {
  procureVendorFormInput: ProcureVendorFormInput;
  setProcureVendorFormInput: Dispatch<SetStateAction<ProcureVendorFormInput>>;
  address: AddressInput;
  setAddress: Dispatch<SetStateAction<AddressInput>>;
  onSubmit: () => void;
};

export default function ProcureVendorForm(props: VendorFormProps) {
  const [openContactPersonModal, setOpenContactPersonModal] = useState<boolean>(false);
  const [anchorElForVendorTrades, setAnchorElForVendorTrades] = useState<HTMLElement | null>(null);
  const [anchorElForAssignedProperties, setAnchorElForAssignedProperties] =
    useState<HTMLElement | null>(null);
  const [contactIndex, setContactIndex] = useState<number | null>(null);

  const onAddVendorContact = (contact: ProcureVendorContactInput) => {
    // if contactIndex exists, we are editing an existing contact
    // when contactIndex === 0, we are editing the first contact in the list
    if (contactIndex || contactIndex === 0) {
      const updatedContacts = props.procureVendorFormInput.procureVendorContacts.map(
        (existingContact: ProcureVendorContactInput, index: number) =>
          index === contactIndex ? contact : existingContact,
      );
      props.setProcureVendorFormInput({
        ...props.procureVendorFormInput,
        procureVendorContacts: updatedContacts,
      });
    }
    // if contextIndex does not exist, we are adding a new contact
    // if length is greater than 0 and contactIndex is null, we are adding a new contact to the existing list
    else if (
      props.procureVendorFormInput.procureVendorContacts.length > 0 &&
      contactIndex === null
    ) {
      props.setProcureVendorFormInput({
        ...props.procureVendorFormInput,
        procureVendorContacts: [...props.procureVendorFormInput.procureVendorContacts, contact],
      });
    } else {
      // if length is 0 and contactIndex is null, we are adding the first contact
      props.setProcureVendorFormInput({
        ...props.procureVendorFormInput,
        procureVendorContacts: [contact],
      });
    }
  };

  const onContactRemoval = (index: number) => {
    props.setProcureVendorFormInput({
      ...props.procureVendorFormInput,
      procureVendorContacts: props.procureVendorFormInput.procureVendorContacts.filter(
        (contact: ProcureVendorContactInput, i: number) => i !== index,
      ),
    });
  };

  const onRemoveIssueType = (issueType: IssueType) => {
    props.setProcureVendorFormInput({
      ...props.procureVendorFormInput,
      issueTypeIds: props.procureVendorFormInput.issueTypeIds.filter(
        (id: string) => id !== issueType.id,
      ),
    });
  };

  const onRemoveProperty = (property: Property) => {
    props.setProcureVendorFormInput({
      ...props.procureVendorFormInput,
      propertyIds: props.procureVendorFormInput.propertyIds.filter(
        (id: string) => id !== property.id,
      ),
    });
  };

  const onVendorContactSubmit = (contact: ProcureVendorContactInput) => {
    setOpenContactPersonModal(false);
    onAddVendorContact(contact);
  };

  const { data: issueTypeData } = useQuery(getIssueTypes);
  const { data: propertyData } = useQuery(getProperties);

  const issueTypes = issueTypeData?.getIssueTypes ?? [];
  const properties = propertyData?.getProperties ?? [];

  return (
    <Grid container spacing={3} paddingY={2}>
      <Grid item>
        <Typography variant={'h6'}>Create Vendor</Typography>
      </Grid>
      {/*Name Field*/}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <TextField
            size={'small'}
            label={'Name'}
            variant='outlined'
            value={props.procureVendorFormInput.name}
            onChange={e =>
              props.setProcureVendorFormInput({
                ...props.procureVendorFormInput,
                name: e.target.value,
              })
            }
          />
        </FormControl>
      </Grid>
      {/*Phone Field*/}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <TextField
            size={'small'}
            label={'Phone'}
            variant='outlined'
            value={props.procureVendorFormInput.phone}
            onChange={e =>
              props.setProcureVendorFormInput({
                ...props.procureVendorFormInput,
                phone: e.target.value,
              })
            }
          />
        </FormControl>
      </Grid>
      {/*Email field*/}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <TextField
            size={'small'}
            label={'Email'}
            variant='outlined'
            value={props.procureVendorFormInput.email}
            onChange={e =>
              props.setProcureVendorFormInput({
                ...props.procureVendorFormInput,
                email: e.target.value,
              })
            }
          />
        </FormControl>
      </Grid>

      {/*Only Render if the vendor is not being edited*/}
      {!props.procureVendorFormInput.id && (
        <>
          {/*Vendor Contact persons*/}
          <Grid item container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Grid item>
              <Typography>Contact persons</Typography>
            </Grid>
            <Grid item>
              <IconButton
                onClick={e => {
                  setContactIndex(null);
                  setOpenContactPersonModal(true);
                }}
              >
                <AddCircleRoundedIcon />
              </IconButton>
            </Grid>
          </Grid>
          {/*Vendor Contact Chips*/}
          {props.procureVendorFormInput.procureVendorContacts.length > 0 && (
            <Grid item container spacing={1}>
              {props.procureVendorFormInput.procureVendorContacts.map(
                (contact: ProcureVendorContactInput, index: number) => (
                  <Grid item key={contact.fullName}>
                    <Chip
                      label={contact.fullName}
                      onClick={() => {
                        setContactIndex(index);
                        setOpenContactPersonModal(true);
                      }}
                      onDelete={() => onContactRemoval(index)}
                    />
                  </Grid>
                ),
              )}
            </Grid>
          )}
        </>
      )}

      <Grid item xs={12}>
        <Divider />
      </Grid>
      {/*Address field*/}
      <Grid item container>
        <Grid item>
          <Typography>Vendor Address input</Typography>
        </Grid>
        <Grid item xs={12}>
          <GoogleAddressForm
            addressInputFormData={props.address}
            setAddressInputFormData={props.setAddress}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/*Vendor Trades*/}
      <Grid container item xs={12} sx={{ alignItems: 'center' }}>
        <Grid item>
          <Typography>Vendor trades</Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={e => setAnchorElForVendorTrades(e.currentTarget)}>
            <AddCircleRoundedIcon />
          </IconButton>
        </Grid>
      </Grid>
      {props.procureVendorFormInput.issueTypeIds.length > 0 && (
        <Grid item container spacing={1} xs={12}>
          {issueTypes
            .filter((issueType: IssueType) =>
              props.procureVendorFormInput.issueTypeIds.includes(issueType.id),
            )
            .map((issueType: IssueType) => (
              <Grid item key={issueType.id}>
                <Chip label={issueType.name} onDelete={() => onRemoveIssueType(issueType)} />
              </Grid>
            ))}
        </Grid>
      )}
      {/*Menu for issue types*/}
      <DropdownWithCheckboxes
        entities={issueTypes}
        onClose={() => setAnchorElForVendorTrades(null)}
        onSave={() => {}}
        anchorEl={anchorElForVendorTrades}
        selectedIds={props.procureVendorFormInput.issueTypeIds}
        setSelectedIds={ids =>
          props.setProcureVendorFormInput({ ...props.procureVendorFormInput, issueTypeIds: ids })
        }
        displayFieldKey={'name'}
      />

      {/*Assigned properties*/}
      <Grid container item xs={12} sx={{ alignItems: 'center' }}>
        <Grid item>
          <Typography>Assigned properties</Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={e => setAnchorElForAssignedProperties(e.currentTarget)}>
            <AddCircleRoundedIcon />
          </IconButton>
        </Grid>
      </Grid>
      {props.procureVendorFormInput.propertyIds.length > 0 && (
        <Grid item container spacing={1} xs={12}>
          {properties
            .filter((property: Property) =>
              props.procureVendorFormInput.propertyIds.includes(property.id),
            )
            .map((property: Property) => (
              <Grid item key={property.id}>
                <Chip label={property.name} onDelete={() => onRemoveProperty(property)} />
              </Grid>
            ))}
        </Grid>
      )}
      {/*Menu for properties*/}
      <DropdownWithCheckboxes
        entities={properties}
        onClose={() => setAnchorElForAssignedProperties(null)}
        onSave={() => {}}
        anchorEl={anchorElForAssignedProperties}
        selectedIds={props.procureVendorFormInput.propertyIds}
        setSelectedIds={ids =>
          props.setProcureVendorFormInput({ ...props.procureVendorFormInput, propertyIds: ids })
        }
        displayFieldKey={'name'}
      />

      <Grid item xs={12}>
        <PrimaryButton sx={{ width: '100%' }} onClick={props.onSubmit}>
          Submit
        </PrimaryButton>
      </Grid>

      {/*Create Vendor Contact Modal*/}
      <Modal
        title={'Create contact person'}
        open={openContactPersonModal}
        setOpen={setOpenContactPersonModal}
      >
        <ProcureVendorContactForm
          vendorContactInput={
            contactIndex || contactIndex === 0
              ? props.procureVendorFormInput.procureVendorContacts[contactIndex]
              : {
                  fullName: '',
                  role: '',
                  phone: '',
                  email: '',
                  notes: '',
                }
          }
          onSubmit={onVendorContactSubmit}
          buttonText={'Create contact'}
        />
      </Modal>
    </Grid>
  );
}
